import { Avatar, AvatarFallback, AvatarImage } from '@src/design-system/avatar';
import { buttonVariants } from '@src/design-system/button';
import { cn } from '@src/design-system/helpers';
import { createRoute, PATHS } from '@src/core/router/paths';
import { MoveLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Copy, TickSquare } from 'iconsax-react';
import { type FC, useState } from 'react';
import { copy } from '@shared/utils/helpers/copy';
import { UserActions } from './user-actions';
import type { TMember } from '@shared/types/member';
import { getUserActivity } from '../../helper';
import { UserContactInfo } from '../user-contact-info';

const UserHeader: FC<{ member: TMember }> = ({ member }) => {
  const { user } = member;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copy(user.id.toString());
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  return (
    <div className='flex justify-between items-center'>
      <div className='flex items-center gap-2'>
        <Link
          to={createRoute(PATHS.USERS)}
          className={cn(
            buttonVariants({ size: 'iconLg', variant: 'primaryOutline' }),
            'rounded-full border-pale-border'
          )}
        >
          <MoveLeftIcon />
        </Link>

        <div className='flex items-center gap-2'>
          <Avatar className='size-16'>
            <AvatarImage src={user.avatar} />
            <AvatarFallback
              className='text-2xl'
              colorId={1}
            >
              {user.first_name.substring(0, 1)}
              {user.last_name.substring(0, 1)}
            </AvatarFallback>
          </Avatar>

          <div className='flex flex-col items-start text-gray-800 truncate'>
            <p className='font-medium flex gap-2'>
              {user.first_name} {user.last_name}
              {copied ? (
                <TickSquare
                  size='16'
                  color='#827599'
                />
              ) : (
                <Copy
                  onClick={handleCopy}
                  size='16'
                  color='#827599'
                  cursor='pointer'
                />
              )}
            </p>
            <div className='flex items-center gap-3 text-base font-light text-gray-600'>
              <p>{user.email}</p>
              <span className='-mt-2 align-top text-xl font-bold'>.</span>
              <p
                className={
                  getUserActivity(user.last_login) === 'Active Now'
                    ? 'text-green-400'
                    : 'text-gray-600'
                }
              >
                {getUserActivity(user.last_login)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='flex gap-2'>
        <UserContactInfo member={member} />
        <UserActions user={user} />
      </div>
    </div>
  );
};

export default UserHeader;
